import { ConcurrencySweepChart, CostComparisonChart } from '@/components/charts';
import type { Block } from '@/components/investor';
import type { DocSection } from '@/components/investor';

/**
 * DialogMe investor one-pager. Mirrors docs/investors/one-pager.md; every figure
 * comes from docs/business-plan/dialogme-business-plan.md — keep the three in sync.
 */

export const onePagerSummary =
  '**DialogMe automates customer communication — all the way to the transaction.** Not a chatbot that answers and hands off. A managed agentic platform: the business connects its channels and systems once, and the agent handles the whole conversation — answering from the business’s own content, showing products, booking appointments, filing tickets and taking payment, on the channels its customers already use. One subscription, priced per conversation, on inference DialogMe owns rather than rents.';

export const onePagerStatus =
  'Open beta, pre-revenue · Spain / EU · Self-funded through development · Seed modelled at €0.8M–€1.5M once ≈100 accounts pay';

export const onePagerTop: Block[] = [
  {
    kind: 'metrics',
    items: [
      { value: '6× – 11×', label: 'Cheaper per interaction than published incumbent rates' },
      { value: '83–87%', label: 'Gross margin on the platform — owned inference, not a rented API' },
      { value: '2.04×', label: 'Throughput of the Fnk stack DialogMe runs on, over the assembled open-source stack' },
      { value: '€0', label: 'External funding required in any scenario', tone: 'plain' },
    ],
  },
  {
    kind: 'text',
    text: 'DialogMe runs on its own AI stack, Fnk — [fnk.ai](https://fnk.ai). This page is about the platform; the stack and the benchmark are documented there.',
  },
];

export const onePagerSections: DocSection[] = [
  {
    title: 'Vision, mission and positioning',
    blocks: [
      {
        kind: 'text',
        text: '**Vision:** LLMs are the new interface to business — buttons, menus and forms give way to human-like conversation, in text or by voice. **Mission:** give every small business the customer conversation that only large companies could afford — answered in seconds, day or night, and finished. **Positioning:** *the receptionist that finishes the job* — the managed platform that books, sells and takes payment inside the conversation, priced per conversation rather than per seat because the inference is owned.',
      },
    ],
  },
  {
    title: 'Problem',
    blocks: [
      {
        kind: 'text',
        text: 'Customers stopped wanting to click — they want to say what they need, and increasingly they delegate the task to an AI assistant. What small businesses can offer them has not moved: a bot that deflects FAQs and hands off the moment there is money involved. Orders, bookings and questions arrive 24/7; most businesses answer during working hours. The agents that genuinely act — Sierra, Decagon, Fin — are priced per resolution or per six-figure contract, for enterprises.',
      },
      {
        kind: 'callout',
        title: 'The real blocker',
        text: '**It is not the agent — it is everything the agent has to reach.** The channel, the catalog, the calendar, the ticketing system and the payment provider: each a separate project, each maintained forever.',
      },
    ],
  },
  {
    title: 'Solution — one agentic platform, message to transaction',
    blocks: [
      {
        kind: 'matrix',
        head: ['', 'What the agent does'],
        rows: [
          ['Answer', 'Grounded in the business’s own documents and catalog'],
          ['Show and recommend', 'Product grids rendered inside the conversation'],
          ['Book', 'Real availability, confirmed and written back to the calendar'],
          ['Take payment', 'Checkout in-chat, behind an explicit confirmation gate'],
          ['Escalate', 'Structured tickets with full context, clean human handover'],
        ],
        minWidth: '420px',
      },
      {
        kind: 'text',
        text: '**Intent → grounded retrieval → a tool call into the real system → a confirmation gate on anything that costs money → the transaction.** Five service types ship preconfigured with the **Fnk Harness** pack for each. Every service is also an **MCP endpoint**, so external AI agents can transact with the business directly.',
      },
    ],
  },
  {
    title: 'The integration surface — this is the product',
    blocks: [
      {
        kind: 'matrix',
        head: ['Layer', 'Integrations — bold is live today'],
        rows: [
          ['Channels', '**Telegram** · WhatsApp · Facebook · Instagram · Web widget'],
          ['Ticketing', '**DialogMe** · Jira · Zendesk'],
          ['Booking', '**Cal.com** · Calendly · Google Calendar · Acuity'],
          ['Content', '**DialogMe** · WordPress · Ghost · Sanity.io · Medium'],
          ['Subscriptions', '**Ko-fi** · **Buy Me a Coffee** · Patreon'],
          ['Catalog and payments', 'Shopify · Medusa · Saleor · Commerce Layer · Stripe · PayPal · BitPay · Coinbase'],
        ],
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Stated plainly',
        text: '**Shop and Payment close the revenue loop, and both are still in build** — this document does not pretend otherwise.',
      },
    ],
  },
  {
    title: 'Built on Fnk — owned inference',
    blocks: [
      {
        kind: 'text',
        text: 'DialogMe runs on its own AI stack, **Fnk** ([fnk.ai](https://fnk.ai)) — the Harness, the agent framework and the reactive inference engine beneath the service. On **21 August 2026** that stack completed **2.04× the requests per second** at **36% lower mean latency** than Spring MVC + LangChain4j + vLLM on the same NVIDIA GPU, leading at every point of a five-point concurrency sweep. **For DialogMe that means the same GPU serves about twice the conversations** — which is what makes €39 for 250 of them a business.',
      },
      { kind: 'node', node: <ConcurrencySweepChart /> },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Two honest limits',
        text: 'One campaign, not yet independently reproduced. And **a written, transferable licence for the Fnk stack is an open item**, to be signed before any external investor enters.',
      },
    ],
  },
  {
    title: 'Business model — priced per conversation',
    blocks: [
      {
        kind: 'matrix',
        head: ['Plan', 'Price / mo', 'Conversations', 'Rate', 'Margin'],
        rows: [
          ['Free', '€0', '50 in total', '—', '—'],
          ['Starter', '€39', '250', '€0.156', '≈81%'],
          ['Scale', '€299', '3,500', '€0.085', '≈79%'],
          ['Custom', 'Sales', 'Unlimited', 'Contracted', 'Deal'],
        ],
        accentRows: [1, 2],
        numeric: [1, 2, 3, 4],
      },
      { kind: 'node', node: <CostComparisonChart /> },
      {
        kind: 'text',
        text: 'All revenue is subscription. **The number to interrogate is the Scale share**: blended ARPU rising €45 → €60 is the mix moving onto Scale, and a Starter-only base cuts Year-5 revenue by 35%.',
      },
    ],
  },
  {
    title: 'Competition',
    blocks: [
      {
        kind: 'matrix',
        head: ['Company', 'Answers', 'Acts', 'Your customers', 'Priced for SMBs', 'Owns inference'],
        rows: [
          ['DialogMe', '●', '●', '●', '●', '● (Fnk)'],
          ['Sierra, Decagon, Fin', '●', '●', '●', '✖', '✖'],
          ['Tidio, ManyChat, Chatbase', '◐', '◐', '●', '●', '✖'],
          ['Botpress, Voiceflow', '●', '◐', '●', '◐', '✖'],
          ['Chatwoot (self-hosted)', '◐', '✖', '●', '●', '✖'],
        ],
        accentRows: [0],
      },
      {
        kind: 'text',
        text: '● core · ◐ partial · ✖ not offered. The rare combination is **customer-facing conversational transactions, priced for a small business, on inference the vendor owns.** Our weakest published comparison is the entry tier — Chatbase at $19 is cheaper than €39.',
      },
    ],
  },
  {
    title: 'Market and financials',
    blocks: [
      {
        kind: 'text',
        text: '≈130,000 Spanish firms fit the profile, ≈1,100,000 across the EU; the base case’s ≈660 paying accounts at Year 5 are **≈0.05%** of that frame. Spanish firms with 10–49 employees sit at 8.7% AI adoption against 49.2% for large firms — the gap is the market.',
      },
      {
        kind: 'matrix',
        head: ['Scenario', 'Development', 'First revenue', 'Year-5 revenue', 'EBITDA-positive', 'Coverage'],
        rows: [
          ['Pessimistic', '24 months', 'Year 3 — €13,104', '€81,600', 'Not inside five years', '1.01×'],
          ['Base', '12 months', 'Year 2 — €39,420', '€409,680', 'Year 4', '1.29×'],
          ['Optimistic', '6 months', 'Year 1 — €38,880', '€1,367,616', 'Year 2', '2.10×'],
        ],
        accentRows: [1],
        numeric: [3, 5],
        minWidth: '640px',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        text: 'Development costs €22,076 in year one and €17,976 a year thereafter, founder unpaid, funded by three €50,000 founder rounds. **€0 of external funding is needed in any scenario — only just in the pessimistic one**, which ends Year 5 with €1,515 left. The eighteen months between best and worst are worth €1,286,016 of Year-5 revenue. These are management scenarios, not contracted revenue.',
      },
    ],
  },
  {
    title: 'Strategy and go-to-market',
    blocks: [
      {
        kind: 'text',
        text: '**Five choices:** win one wedge (appointment businesses on Telegram) before widening; small business first; own the inference and price the conversation; self-serve is the product; earn every step with paying accounts.',
      },
      {
        kind: 'matrix',
        head: ['Paying accounts', 'What it buys'],
        rows: [
          ['≈10 live', 'Self-serve works unassisted; first retention signal'],
          ['≈100 paying', 'Two measured cohorts and a case study — the company stops consuming founder capital, and the seed conversation opens'],
          ['≈500–1,000', 'A reference list, vertical proof points, retention history a partner will accept'],
        ],
        accentRows: [1],
      },
      {
        kind: 'text',
        text: 'Hand-onboarded design partners in the appointment verticals first; then concentration on the vertical that retains, through associations, chambers and Kit Digital agents; then agency partners. **Roughly 110–140 paying accounts cover the entire development cost base.**',
      },
    ],
  },
  {
    title: 'Team',
    blocks: [
      {
        kind: 'text',
        text: '**Alisher Alimov · Founder · 14+ years in distributed systems.** Senior systems engineering at NVIDIA. Previously co-founder and CTO of **Gridfore** — R&D to a packaged enterprise platform, delivered to VEON and X5 Retail Group, presented at CES 2019 and MWC Shanghai; co-inventor of the registered Gridfore Reactive Intelligent Platform. Sole author of the DialogMe platform and the Fnk stack beneath it.',
      },
      {
        kind: 'text',
        text: '*Gridfore results are company-level. NVIDIA is not a sponsor, customer, investor or source of venture IP.* One founder, unpaid through development; every hire gated on banked revenue.',
      },
    ],
  },
  {
    title: 'The ask — not yet, and when',
    blocks: [
      {
        kind: 'text',
        text: '**Not raising during development.** The seed conversation opens at ≈100 paying accounts and two measured cohorts, on the base column of the raise model:',
      },
      {
        kind: 'metrics',
        items: [
          { value: '€0.8–1.5M', label: 'Seed modelled' },
          { value: '€4–6M', label: 'Pre-money valuation', tone: 'plain' },
          { value: '15–22%', label: 'Implied dilution', tone: 'plain' },
          { value: '18–24 mo', label: 'Runway at the resulting cost base', tone: 'plain' },
        ],
      },
      {
        kind: 'text',
        text: 'Engineering 40% (Shop and Payment first, then WhatsApp and the web widget) · go-to-market 35% · support 10% · security, working capital and reserve 15%. **If the first market year closes under fifty paying accounts**, a €200,000–€400,000 pre-seed becomes the prudent move — and this document says so in advance.',
      },
      {
        kind: 'callout',
        title: 'Where it goes',
        text: 'The modelled exit is a small, profitable European SaaS company at €2m–€14m. Beyond it: **the agent layer small businesses reach their other software through** — in no figure above. The stack underneath: [fnk.ai](https://fnk.ai).',
      },
    ],
  },
];
